import { useVirtualizer } from '@tanstack/react-virtual';
import { useSetState } from 'ahooks';
import { useEffect, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Loading } from '@/app/components/icons/Loading';
import { useFetch } from '@/app/hooks';
import { cn } from '@/app/utils';
import { apiGetMyCreation } from './apis';
import type { MyCreationsSelectorMultiProps, MyCreationsSelectorProps, MyCreationsSelectorState } from './types';

export type { MyCreationsSelectorProps } from './types';

const EMPTY_SELECTED_INDICES: number[] = [];

export function MyCreationsSelector(props: MyCreationsSelectorProps) {
  const {
    types,
    isFavor = false,
    emptyText = 'No creations available',
    emptyFavorText = 'No favorite creations',
    columnCount = 3,
    rowHeight = 200,
    gap = 4,
    pageSize = 30,
    className,
    initialCreation,
    onDataLoad,
    renderItem,
    getItemKey,
    isItemDisabled,
    itemClassName,
    rowItemClassName,
  } = props;

  const multiple = props.multiple === true;
  const selectedIndex = !multiple ? (props.selectedIndex ?? null) : null;
  const selectedIndices = multiple ? (props as MyCreationsSelectorMultiProps).selectedIndices : EMPTY_SELECTED_INDICES;
  const onSelectionChange = !multiple ? props.onSelectionChange : undefined;
  const onMultiSelectionChange = multiple ? (props as MyCreationsSelectorMultiProps).onMultiSelectionChange : undefined;
  const maxMultiSelect = multiple ? (props as MyCreationsSelectorMultiProps).maxMultiSelect : undefined;
  const onMultiSelectLimitReached = multiple
    ? (props as MyCreationsSelectorMultiProps).onMultiSelectLimitReached
    : undefined;

  const [state, setState] = useSetState<MyCreationsSelectorState>({
    creations: [],
    total: 0,
    page: 0,
    selectedIndex,
    selectedIndices,
    isFavor,
  });

  const parentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setState({
      page: 0,
      selectedIndex: null,
      selectedIndices: [],
    });
    if (multiple) {
      onMultiSelectionChange?.([], []);
    }
  }, [state.isFavor]);

  useEffect(() => {
    if (multiple) {
      setState({ selectedIndices, selectedIndex: null, isFavor });
    } else {
      setState({ selectedIndex, selectedIndices: [], isFavor });
    }
  }, [multiple, selectedIndex, selectedIndices, isFavor]);

  const { loading: loadingCreations } = useFetch(
    () => {
      return apiGetMyCreation({
        is_favor: state.isFavor,
        'pager.page': state.page,
        'pager.pagesz': pageSize,
        types,
      });
    },
    {
      onSuccess: ({ creations, total }) => {
        const newCreations = state.page === 0 ? creations : state.creations.concat(creations);
        setState({
          creations: newCreations,
          total: total,
        });
        onDataLoad?.(newCreations, total);
      },
      ready: !initialCreation,
      refreshDeps: [state.page, state.isFavor, types],
    },
  );

  const rowVirtualizer = useVirtualizer({
    count: Math.ceil(state.creations.length / columnCount),
    estimateSize: () => rowHeight + gap,
    getScrollElement: () => parentRef.current,
    overscan: 2,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  const handleSelect = (index: number) => {
    const disabled = isItemDisabled?.(state.creations[index]) ?? false;

    if (multiple) {
      if (disabled) return;
      const prev = state.selectedIndices;
      const pos = prev.indexOf(index);
      if (pos < 0 && typeof maxMultiSelect === 'number' && maxMultiSelect > 0 && prev.length >= maxMultiSelect) {
        onMultiSelectLimitReached?.();
        return;
      }
      const nextIndices = pos >= 0 ? prev.filter((i) => i !== index) : [...prev, index];
      const nextCreations = nextIndices.map((i) => state.creations[i]);
      setState({ selectedIndices: nextIndices });
      onMultiSelectionChange?.(nextIndices, nextCreations);
      return;
    }

    if (disabled) {
      setState({ selectedIndex: null });
      onSelectionChange?.(null, undefined);
      return;
    }

    setState({ selectedIndex: index });
    onSelectionChange?.(index, state.creations[index]);
  };

  const handleLoadMore = () => {
    setState({ page: state.page + 1 });
  };

  const hasMore = state.creations.length < state.total;

  return (
    <div id="scrollableTarget" ref={parentRef} className={cn('relative h-full overflow-y-auto', className)}>
      {!loadingCreations && state.creations.length === 0 ? (
        <div className="size-full rounded-8 bg-system-black24">
          <span className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 max-w-40 text-center font-medium text-sm leading-[1.4]">
            {state.isFavor ? emptyFavorText : emptyText}
          </span>
        </div>
      ) : (
        <InfiniteScroll
          dataLength={state.creations.length}
          next={handleLoadMore}
          hasMore={hasMore}
          loader={null}
          scrollThreshold={0.5}
          scrollableTarget="scrollableTarget"
        >
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              position: 'relative',
              width: '100%',
            }}
          >
            {virtualItems.map((virtualRow) => {
              const rowStartIndex = virtualRow.index * columnCount;
              const rowItems = state.creations.slice(rowStartIndex, rowStartIndex + columnCount);

              return (
                <div
                  key={virtualRow.key}
                  style={{
                    height: `${rowHeight}px`,
                    left: 0,
                    position: 'absolute',
                    top: 0,
                    transform: `translateY(${virtualRow.start}px)`,
                    width: '100%',
                  }}
                  className={cn('grid grid-cols-3 gap-1', itemClassName)}
                >
                  {rowItems.map((creation, columnIndex) => {
                    const index = rowStartIndex + columnIndex;
                    const isSelected = multiple ? state.selectedIndices.includes(index) : state.selectedIndex === index;
                    const disabled = isItemDisabled?.(creation) ?? false;
                    const key = getItemKey?.(creation) ?? creation.id ?? index;

                    return (
                      <div
                        key={key}
                        onClick={() => handleSelect(index)}
                        className={cn(
                          'relative aspect-square w-full overflow-hidden rounded-6 before:absolute before:inset-0 before:z-10 before:rounded-6 before:border-2 before:border-transparent before:border-solid before:content-[""]',
                          { 'before:border-system-blue02': isSelected },
                          { 'opacity-40': disabled },
                          { 'cursor-pointer': !disabled },
                          rowItemClassName,
                        )}
                      >
                        {renderItem(creation, index, isSelected)}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </InfiniteScroll>
      )}

      {loadingCreations && state.creations.length === 0 && (
        <div className="size-full rounded-8 bg-system-black24">
          <div className="absolute top-[50%] left-[50%] max-w-40 translate-x-[-50%] translate-y-[-50%] text-center font-medium text-sm leading-[1.4]">
            <Loading className="animate-spin text-base" />
          </div>
        </div>
      )}
    </div>
  );
}
